<template lang="pug">
    .csv-import(v-loading="validating || initializing")
        transition-group(name="slide-decent-vertical" mode="out-in")
            .import-info-panel(key="prev-import-info")
                .alert.info(v-if="overview && overview.previous && overview.previous.status === 'failed'")
                    i.el-icon-info
                    .text
                        b {{ $t('The most recent import failed') }}
                        p {{ $t('File: {file}', { file: overview.previous.file.name }) }}
                        p(v-if="overview.previous.error") {{ $t('Error: {error}', { error: overview.previous.error }) }}
            .import-info-panel(v-if="overview && overview.current" key="import-info")
                .fresh(v-if="overview.current.status === 'fresh'")
                    .alert.primary
                        p {{ $t('Your import has been queued and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                .pending(v-if="overview.current.status === 'pending'")
                    .alert.primary
                        p {{ $t('An import is in the queue and will start as soon as possible.') }}
                        p {{ $t('Please check later') }},&nbsp
                            a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                .running(v-if="overview.current.status === 'running'")
                    .alert.primary
                        .percentage
                            el-progress(
                                type="dashboard"
                                :width="100"
                                :percentage="overview.current.percentage"
                                :format="percentage => `${Math.round(percentage * 100) / 100}%`"
                            )
                        .info(v-if="overview.current.percentage < 100 || overview.current.finished_at === null")
                            b {{ $t('An import is currently running.') }}
                            p {{ $t('File: {file}', { file: overview.current.file.name }) }}
                            p {{ $t('Started: {time}', { time: formatDistanceToNow(overview.current.started_at), percentage: Math.round(overview.current.percentage * 100) / 100 }) }}
                            p(v-if="estimatedCompletion") {{ $t('Estimated completion: {time}.', { time: estimatedCompletion }) }}
                            p {{ $t('Please check later') }},&nbsp
                                a(href="#" @click.stop.prevent="refresh") {{ $t('or refresh now') }}
                        .info(v-else)
                            p {{ $t('The import has been completed.') }}

            .demo-csv-hint(
                key="demo-csv-hint"
                v-if="validationResult === undefined || (validationResult !== undefined && validationResult.status === 'invalid')"
            )
                p.demo-csv-hint-intro {{ $t('csv-hint-intro') }}
                p.demo-csv-hint-intro {{ $t('csv-hint-update') }}
                table.csv-hint-table
                    thead
                        tr
                            th {{ $t('csv-hint-col-scenario') }}
                            th {{ $t('csv-hint-col-required') }}
                            th {{ $t('csv-hint-col-fields') }}
                    tbody
                        tr
                            td {{ $t('csv-hint-scenario-base') }}
                            td
                                code id
                                |  {{ $t('or') }}
                                code email
                            td {{ $t('csv-hint-fields-base') }}
                        tr(v-if="hasShop")
                            td {{ $t('csv-hint-scenario-shop') }}
                            td
                                code id
                                | ,&nbsp;
                                code nr
                                |  {{ $t('or') }}
                                code email
                            td {{ $t('csv-hint-fields-shop') }}
                        tr
                            td {{ $t('csv-hint-scenario-newsletter') }}
                            td
                                code email
                            td {{ $t('csv-hint-fields-newsletter') }}
                .demo-csv-hint-actions
                    el-button(
                        size="mini"
                        type="primary"
                        plain
                        icon="el-icon-fa-download fas"
                        @click="downloadDemoCsv('base')"
                    ) {{ $t('csv-download-base') }}
                    el-button(
                        v-if="hasShop"
                        size="mini"
                        type="primary"
                        plain
                        icon="el-icon-fa-download fas"
                        @click="downloadDemoCsv('shop')"
                    ) {{ $t('csv-download-shop') }}
                    el-button(
                        size="mini"
                        type="primary"
                        plain
                        icon="el-icon-fa-download fas"
                        @click="downloadDemoCsv('newsletter')"
                    ) {{ $t('csv-download-newsletter') }}
            el-upload.plus-box(
                key="upload"
                v-if="validationResult === undefined || (validationResult !== undefined && validationResult.status === 'invalid')"
                ref="upload"
                drag :action="uploadEndpoint"
                accept="text/csv"
                :before-upload="validateUpload"
                :on-change="updateUploadHeaders"
                :on-success="uploadSucceed"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
            )
                i.el-icon-plus
                .el-upload__text(v-html="$t('upload.drop.singular')")
                .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'CSV', size: '20mb' }) }}
            .validation-result(
                v-if="validationResult !== undefined"
                key="validation"
            )
                template(v-if="validationResult.status === 'valid'")
                    .alert
                        p {{ $t('The columns of the uploaded import file could be fully recognized.') }}
                        p {{ $t('You can now start the import.') }}
                    el-button(
                        size="mini"
                        icon="el-icon-fa-times fas"
                        @click="validationResult = undefined"
                    ) {{ $t('Cancel') }}
                    el-button(
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-check fas"
                        @click="start"
                    ) {{ $t('Start import') }}
                .alert.danger(v-else)
                    p
                        b {{ $t('The uploaded CSV ist invalid.') }}
                    ol
                        li(v-if="validationResult.missing_identifier === true")
                            p(v-if="hasShop") {{ $t('Missing identifier. There must be either an "ID", "NR" or "Email" column.') }}
                            p(v-else) {{ $t('Missing identifier. There must be either an "ID" or "Email" column.') }}
                        li(v-if="validationResult.shop_only_fields && validationResult.shop_only_fields.length")
                            p {{ $t('The following fields require the shop extension.') }}
                            ul
                                li(v-for="field in validationResult.shop_only_fields") {{ field }}
                        li(v-if="validationResult.invalid_fields && validationResult.invalid_fields.length")
                            p {{ $t('The following fields are not supported.') }}
                            ul
                                li(v-for="field in validationResult.invalid_fields") {{ field }}
                        li(v-if="validationResult.invalid_lines && validationResult.invalid_lines.length")
                            p {{ $t('The following lines are invalid. They have an unexpected number of columns.') }}
                            p {{ $t('The expected amount of fields is {number}', { number: validationResult.expected_fields }) }}
                            ul
                                li(v-for="element in validationResult.invalid_lines") {{ $t('Line {line} contains {fields} fields', { line: element.key, fields: element.value }) }}
                    p
                        b {{ $t('Check your import file and try again after said issues has been fixed.') }}
</template>

<i18n>
{
    "en": {
        "It has been started {time} ago and is currently at {percentage}%.": "It has been started {time} ago and is currently at {percentage}%.",
        "File: {file}": "File: {file}",
        "Error: {error}": "Error: {error}",
        "Started: {time}": "Started: {time}",
        "The Last import has been finished successfully {time} ago.": "The Last import has been finished successfully {time} ago.",
        "The last import could not be completed successfull. It failed {time} ago.": "The last import could not be completed successfull. It failed {time} ago.",
        "Estimated completion: {time}.": "Estimated completion: {time}.",
        "Line {line} contains {fields} fields": "Line {line} contains {fields} fields",
        "The expected amount of fields is {number}": "The expected amount of fields is {number}",
        "Import {number} people": "Import {number} people"
    },
    "de": {
        "Something went wrong!": "Etwas ist schiefgelaufen!",
        "The uploaded CSV ist invalid.": "Die hochgeladene CSV ist ungültig.",
        "The columns of the uploaded import file could be fully recognized.": "Die Spalten der hochgeladenen Importdatei konnten vollständig erkannt/zugeordnet werden.",
        "The columns of the uploaded import file could not be fully recognized.": "Die Spalten der hochgeladenen Importdatei konnten nicht vollständig erkannt/zugeordnet werden.",
        "You can now start the import.": "Der Import kann jetzt gestartet werden",
        "Start import": "Import starten",
        "Cancel": "Abbrechen",
        "Missing identifier. There must be either an \"ID\", \"NR\" or \"Email\" column.": "Es fehlt die Spalte zur Kennung. Es muss entweder die Spalte \"ID\", \"NR\" oder \"Email\" vorhanden sein.",
        "Missing identifier. There must be either an \"ID\" or \"Email\" column.": "Es fehlt die Spalte zur Kennung. Es muss entweder die Spalte \"ID\" oder \"Email\" vorhanden sein.",
        "Check your import file and try again after said issues has been fixed.": "Überprüfen Sie Ihre Importdatei und versuchen Sie es erneut, nachdem die genannten Probleme behoben wurden.",
        "The following fields are not supported.": "Die folgenden Felder werden nicht unterstützt.",
        "The following fields require the shop extension.": "Die folgenden Felder erfordern die Shop-Erweiterung.",
        "The following lines are invalid. They have an unexpected number of columns.": "Die folgenden Zeilen haben eine unerwartete Anzahl an Spalten.",
        "Line {line} contains {fields} fields": "Zeile {line} enthält {fields} Spalten",
        "The expected amount of fields is {number}": "Die erwartete Anzahl an Spalten ist {number}",
        "The most recent import failed": "Der zuletzt ausgeführte Import ist fehlgeschlagen",
        "Technical issue! Import could not be initialized.": "Technisches Problem! Import konnte nicht initialisiert werden.",
        "Import could not be refreshed.": "Import konnte nicht aktualisiert werden.",
        "Import could not be started.": "Import konnte nicht gestartet werden.",
        "An import is in the queue and will start as soon as possible.": "Aktuell befindet sich bereits ein Import in der Warteschlange und wird so schnell wie möglich gestartet.",
        "An import is currently running.": "Aktuell befindet sich bereits ein Import in der Verarbeitung.",
        "Import {number} people": "Importiere {number} Personen",
        "It has been started {time} ago and is currently at {percentage}%.": "Der Import wurde vor {time} gestartet und ist aktuell bei {percentage}%.",
        "File: {file}": "Datei: {file}",
        "Started: {time}": "Gestartet vor: {time}",
        "The Last import has been finished successfully {time} ago.": "Der letzte import wurde erfolgreich abgeschlossen vor {time}.",
        "The last import could not be completed successfull. It failed {time} ago.": "Der letzte Import konnte nicht erfolgreich abgeschlossen werden. Fehlgeschlagen vor {time}.",
        "Estimated completion: {time}.": "Voraussichtliche Dauer bis zur Fertigstellung: {time}",
        "Your import has been queued and will start as soon as possible.": "Dein Import wurde erfolgreich erfasst und wird so schnell wie möglich gestartet.",
        "Please check later": "Schau doch später nochmal vorbei",
        "The import has been completed.": "Der Import wurde erfolgreich abgeschlossen.",
        "or refresh now": "oder aktualisiere jetzt",
        "Import": "Importieren",
        "Try again": "Erneut versuchen",
        "No import file yet?": "Noch keine Importdatei?",
        "or": "oder",
        "csv-hint-intro": "Laden Sie eine passende Demo-CSV herunter und passen Sie diese an Ihre Daten an. Je nach Szenario gelten unterschiedliche Pflichtfelder und erlaubte Spalten.",
        "csv-hint-update": "Der Import kann sowohl neue Mitglieder anlegen als auch bestehende Datensätze ergänzen oder aktualisieren – vorausgesetzt, das Pflichtfeld (ID oder E-Mail) stimmt mit einem vorhandenen Eintrag überein.",
        "csv-hint-col-scenario": "Szenario",
        "csv-hint-col-required": "Pflichtfeld(er)",
        "csv-hint-col-fields": "Verfügbare Felder",
        "csv-hint-scenario-base": "Standard",
        "csv-hint-scenario-shop": "Mit Shop-Erweiterung",
        "csv-hint-scenario-newsletter": "Nur Newsletter",
        "csv-hint-fields-base": "Vorname, Nachname, Anrede, Geschlecht, Telefon, Sprache, E-Mail-Verifizierung, Firma, Adresse, Tags, Abonnement-Themen, Überspringen",
        "csv-hint-fields-shop": "Alle Standard-Felder + Kundennr., Kundengruppe, Steuerpflicht, Nettopreise, USt-IdNr.",
        "csv-hint-fields-newsletter": "E-Mail + optional Vorname, Nachname, Abonnement-Themen",
        "csv-download-base": "Demo-CSV (Standard)",
        "csv-download-shop": "Demo-CSV (Shop)",
        "csv-download-newsletter": "Demo-CSV (Newsletter)"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { formatDistanceToNow, fromUnixTime, getUnixTime } from 'date-fns';

import { adminClient as apolloClient } from '@/store/apollo';

import { isTokenValidOrUndefined, fetchAccessToken } from '@/helpers'

import AuthApi from '@/store/api/system/auth';
import ProductCsvImport from '@/extensions/shop/components/Products/ProductCsvImport';
import { useRestrictedAreaMemberStore, useSetupStore } from '@/stores';

export default {
    name: 'MemberCsvImport',
    extends: ProductCsvImport,
    computed: {
        memberStore() {
            return useRestrictedAreaMemberStore()
        },
        hasShop() {
            return !!_.get(this.activeCms, 'shop.id');
        },
    },
    methods: {
        downloadDemoCsv(mode = 'base') {
            const configs = {
                base: {
                    filename: 'member-import-demo.csv',
                    fields: ['id', 'gender', 'salutation', 'first_name', 'last_name', 'email', 'phone', 'language', 'email_verified', 'tags', 'subscription_topics', 'address_id', 'company', 'company_addition', 'street', 'zipcode', 'city', 'country', 'default_billing', 'default_shipping', 'skip'],
                    example: ['', 'm', 'Herr', 'Max', 'Mustermann', 'max@beispiel.de', '+49123456789', 'de', '1', 'VIP;Newsletter', '', '', 'Musterfirma GmbH', '', 'Musterstraße 1', '12345', 'Musterstadt', 'DE', '1', '1', '0'],
                },
                shop: {
                    filename: 'member-import-demo-shop.csv',
                    fields: ['id', 'nr', 'gender', 'salutation', 'first_name', 'last_name', 'email', 'phone', 'language', 'email_verified', 'taxable', 'show_net_prices', 'vat_identification_number', 'group', 'tags', 'subscription_topics', 'address_id', 'company', 'company_addition', 'street', 'zipcode', 'city', 'country', 'default_billing', 'default_shipping', 'skip'],
                    example: ['', '1001', 'm', 'Herr', 'Max', 'Mustermann', 'max@beispiel.de', '+49123456789', 'de', '1', '0', '0', '', 'Standard', 'VIP;Newsletter', '', '', 'Musterfirma GmbH', '', 'Musterstraße 1', '12345', 'Musterstadt', 'DE', '1', '1', '0'],
                },
                newsletter: {
                    filename: 'member-import-demo-newsletter.csv',
                    fields: ['email', 'first_name', 'last_name', 'subscription_topics'],
                    example: ['newsletter@beispiel.de', 'Max', 'Mustermann', 'Newsletter;Angebote'],
                },
            }
            const { filename, fields, example } = configs[mode] || configs.base
            const csv = fields.join(',') + '\n' + example.join(',')
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            a.click()
            URL.revokeObjectURL(url)
        },
        validateCsv() {
            if (this.file) {
                this.validating = true;
                const payload = {
                    shopID: _.get(this.activeCms, 'shop.id'),
                    cmsID: _.get(this.activeCms, 'id'),
                    file: this.file,
                }
                this.memberStore.validateMemberImportCsv(payload)
                    .then(data => {
                        this.validationResult = data;
                        this.file = data.file;
                    })
                    .catch(error => {
                        let message
                        const { graphQLErrors } = error
                        if (graphQLErrors && graphQLErrors.length > 0) {
                            message = graphQLErrors[0].details
                        }

                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Import could not be refreshed.')
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            message,
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.validating = false
                    })
            }
        },
        start() {
            if (this.file) {
                this.initializing = true;
                this.memberStore.initializeMemberImport({
                    shopID: _.get(this.activeCms, 'shop.id'),
                    cmsID: _.get(this.activeCms, 'id'),
                    file: this.file,
                })
                    .then(data => {
                        this.error = false;
                        this.overview = data;
                        this.file = undefined;
                        this.validationResult = undefined;
                    })
                    .catch(error => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Technical issue! Import could not be initialized.')
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.initializing = false
                    });
            } else {
                this.$notify.error({
                    title: this.$t('Something went wrong!'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
            }
        },
        refresh() {
            apolloClient.clearStore().then(() => {
                this.$emit('refresh')
                this.memberStore.initializeMemberImport({
                    shopID: _.get(this.activeCms, 'shop.id'),
                    cmsID: _.get(this.activeCms, 'id'),
                })
                    .then(data => {
                        this.error = false;
                        this.overview = data;
                        this.file = undefined;
                    })
                    .catch(error => {
                        // eslint-disable-next-line
                        console.error(error);
                        this.error = this.$t('Import could not be refreshed.')
                        this.$notify.error({
                            title: this.$t('Something went wrong!'),
                            iconClass: 'fas el-icon-fa-times',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        })
                    })
                    .finally(() => {
                        this.initializing = false
                    });
            });
        },
    },
    mounted() {
        this.initializing = true;
        this.memberStore.initializeMemberImport({
            shopID: _.get(this.activeCms, 'shop.id'),
            cmsID: _.get(this.activeCms, 'id'),
        })
            .then(data => {
                this.overview = data
            })
            .finally(() => {
                this.initializing = false
            })
    }
}
</script>

<style lang="sass" scoped>
    .upload
        width: 100%

    .alert.danger
        ul
            margin: 0.5rem 0
        & > *:last-child
            margin-bottom: 0

    .running .alert
        display: flex
        align-items: center
        .percentage
            margin-right: 1rem
            margin-bottom: -1rem
            ::v-deep
                .el-progress__text
                    padding-left: 0.5rem
                path.el-progress-circle__track
                    stroke: #dad4e5 !important
    .alert.info
        display: flex
        align-items: center
        i
            opacity: 0.6
            min-width: 2rem
            font-size: 1.4rem

    .demo-csv-hint
        background: #f5f7fa
        border: 1px solid #dcdfe6
        border-radius: 6px
        padding: 12px 14px
        margin-bottom: 12px
        font-size: 13px
        color: #606266
        .demo-csv-hint-intro
            margin: 0 0 10px
            line-height: 1.5
        .csv-hint-table
            width: 100%
            border-collapse: collapse
            margin-bottom: 12px
            font-size: 12px
            th, td
                text-align: left
                padding: 5px 8px
                border: 1px solid #dcdfe6
            th
                background: #ebeef5
                font-weight: 500
                color: #303133
            td
                color: #606266
                vertical-align: top
            code
                background: #e6e6e6
                border-radius: 3px
                padding: 1px 4px
                font-size: 11px
                color: #303133
        .demo-csv-hint-actions
            display: flex
            gap: 8px
            flex-wrap: wrap
</style>
